import { useState } from 'react';
import { Box, Typography } from '@mui/material';
import { stripeInputStyles, stripeLabelStyles, stripeSelectStyles } from '../hub-utils';
import { statesList } from '../../shared/misc/us-states';
import { phoneNumberValidator, emailAddressValidator } from '../../shared/misc/regex';
import { Utils } from '../../shared/utils';

export const RELATIONSHIP_OPTIONS = [
  { value: 'Self', label: 'Self' },
  { value: 'Spouse', label: 'Spouse' },
  { value: 'Partner', label: 'Partner' },
  { value: 'Parent', label: 'Parent' },
  { value: 'Grand Parent', label: 'Grand Parent' },
  { value: 'Relative', label: 'Relative' },
  { value: 'Guardian', label: 'Guardian' },
  { value: 'Friend', label: 'Friend' },
  { value: 'Other', label: 'Other' },
];

export interface GuarantorDetails {
  streetAddress1: string;
  city: string;
  state: string;
  zipCode: string;
  phone: string;
  birthDate: string;
  relationshipToPatient: string;
}

export interface AddressDetails extends GuarantorDetails {}

export interface ChampionNameDetails {
  firstName: string;
  lastName: string;
  email: string;
}

export const isGuarantorInfoComplete = (details: GuarantorDetails, isEnrollmentFlow: boolean = true): boolean => {
  const hasValidDob = !!(details.birthDate?.trim());

  // Non-enrollment flow only requires date of birth
  if (!isEnrollmentFlow) {
    return hasValidDob;
  }

  // Enrollment flow requires all fields
  const hasValidAddress = !!(
    details.streetAddress1?.trim() &&
    details.city?.trim() &&
    details.state?.trim() &&
    details.zipCode?.trim()
  );

  const hasValidPhone = !!(
    details.phone?.trim() &&
    phoneNumberValidator.test(details.phone)
  );

  const hasValidRelationship = !!(details.relationshipToPatient?.trim());

  return hasValidAddress && hasValidPhone && hasValidDob && hasValidRelationship;
};

export const isAddressComplete = (address: GuarantorDetails): boolean => {
  return isGuarantorInfoComplete(address);
};

interface GuarantorAddressFieldsProps {
  addressDetails: GuarantorDetails;
  onAddressDetailsChange: (field: keyof GuarantorDetails, value: string) => void;
  testIdPrefix?: string;
  showNameFields?: boolean;
  nameDetails?: ChampionNameDetails;
  onNameDetailsChange?: (field: keyof ChampionNameDetails, value: string) => void;
  isEnrollmentFlow?: boolean;
  isPatientPayor?: boolean;
}

interface TouchedFields {
  streetAddress1: boolean;
  city: boolean;
  state: boolean;
  zipCode: boolean;
  phone: boolean;
  birthDate: boolean;
  relationshipToPatient: boolean;
  firstName: boolean;
  lastName: boolean;
  email: boolean;
}

const GuarantorAddressFields: React.FC<GuarantorAddressFieldsProps> = ({
  addressDetails,
  onAddressDetailsChange,
  testIdPrefix = '',
  showNameFields = false,
  nameDetails,
  onNameDetailsChange,
  isEnrollmentFlow = true,
  isPatientPayor = false,
}) => {
  const [touched, setTouched] = useState<TouchedFields>({
    streetAddress1: false,
    city: false,
    state: false,
    zipCode: false,
    phone: false,
    birthDate: false,
    relationshipToPatient: false,
    firstName: false,
    lastName: false,
    email: false,
  });

  const getTestId = (field: string) => testIdPrefix ? `${testIdPrefix}-${field}` : field;

  const handleBlur = (field: keyof GuarantorDetails | keyof ChampionNameDetails) => {
    setTouched(prev => ({ ...prev, [field]: true }));
  };

  const nonEnrollRequiredFields: (keyof GuarantorDetails)[] = ['birthDate'];

  const getError = (field: keyof GuarantorDetails): string | null => {
    if (!touched[field]) return null;

    if (!isEnrollmentFlow && !nonEnrollRequiredFields.includes(field)) {
      if (field === 'phone' && addressDetails.phone && !phoneNumberValidator.test(addressDetails.phone)) {
        return 'Please enter a valid phone number';
      }
      return null;
    }
    
    const fieldLabels: Record<keyof GuarantorDetails, string> = {
      streetAddress1: 'Street address',
      city: 'City',
      state: 'State',
      zipCode: 'Zip code',
      phone: 'Phone number',
      birthDate: 'Date of birth',
      relationshipToPatient: 'Relationship to patient',
    };

    if (!addressDetails[field]?.trim()) {
      return `${fieldLabels[field]} is required`;
    }

    if (field === 'phone' && addressDetails.phone && !phoneNumberValidator.test(addressDetails.phone)) {
      return 'Please enter a valid phone number';
    }

    return null;
  };

  const getNameError = (field: keyof ChampionNameDetails): string | null => {
    if (!touched[field] || !nameDetails) return null;
    
    const fieldLabels: Record<keyof ChampionNameDetails, string> = {
      firstName: 'First name',
      lastName: 'Last name',
      email: 'Email',
    };

    if (!nameDetails[field]?.trim()) {
      return `${fieldLabels[field]} is required`;
    }

    if (field === 'email' && nameDetails.email && !emailAddressValidator.test(nameDetails.email)) {
      return 'Please enter a valid email address';
    }

    return null;
  };

  const getInputStyle = (field: keyof GuarantorDetails) => ({
    ...stripeInputStyles,
    borderColor: getError(field) ? '#df1b41' : '#e6e6e6',
  });

  const getNameInputStyle = (field: keyof ChampionNameDetails) => ({
    ...stripeInputStyles,
    borderColor: getNameError(field) ? '#df1b41' : '#e6e6e6',
  });

  const getSelectStyle = (field: keyof GuarantorDetails) => ({
    ...stripeSelectStyles,
    borderColor: getError(field) ? '#df1b41' : '#e6e6e6',
  });

  return (
    <Box sx={{ mt: 2 }}>
      {/* Name Fields (optional - for new champion creation) */}
      {showNameFields && nameDetails && onNameDetailsChange && (
        <>
          {/* First Name and Last Name Row */}
          <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
            <Box sx={{ flex: 1 }}>
              <Typography component="label" sx={stripeLabelStyles}>
                First name
              </Typography>
              <input
                type="text"
                value={nameDetails.firstName}
                onChange={(e) => onNameDetailsChange('firstName', e.target.value)}
                onBlur={() => handleBlur('firstName')}
                placeholder="First name"
                style={getNameInputStyle('firstName')}
                test-id={getTestId('first-name')}
              />
              {getNameError('firstName') && (
                <Typography sx={{ color: '#df1b41', fontSize: '12px', mt: 0.5 }}>
                  {getNameError('firstName')}
                </Typography>
              )}
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography component="label" sx={stripeLabelStyles}>
                Last name
              </Typography>
              <input
                type="text"
                value={nameDetails.lastName}
                onChange={(e) => onNameDetailsChange('lastName', e.target.value)}
                onBlur={() => handleBlur('lastName')}
                placeholder="Last name"
                style={getNameInputStyle('lastName')}
                test-id={getTestId('last-name')}
              />
              {getNameError('lastName') && (
                <Typography sx={{ color: '#df1b41', fontSize: '12px', mt: 0.5 }}>
                  {getNameError('lastName')}
                </Typography>
              )}
            </Box>
          </Box>

        </>
      )}

      {/* Phone and Date of Birth Row */}
      <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
        <Box sx={{ flex: 1 }}>
          <Typography component="label" sx={stripeLabelStyles}>
            Phone Number
          </Typography>
          <input
            type="tel"
            value={addressDetails.phone}
            onChange={(e) => onAddressDetailsChange('phone', Utils.formatPhoneNumber(e.target.value))}
            onBlur={() => handleBlur('phone')}
            placeholder="555-555-5555"
            style={getInputStyle('phone')}
            test-id={getTestId('phone')}
            maxLength={12}
          />
          {getError('phone') && (
            <Typography sx={{ color: '#df1b41', fontSize: '12px', mt: 0.5 }}>
              {getError('phone')}
            </Typography>
          )}
        </Box>
        <Box sx={{ flex: 1 }}>
          <Typography component="label" sx={stripeLabelStyles}>
            Date of Birth
          </Typography>
          <input
            type="date"
            value={addressDetails.birthDate}
            onChange={(e) => onAddressDetailsChange('birthDate', e.target.value)}
            onBlur={() => handleBlur('birthDate')}
            style={getInputStyle('birthDate')}
            test-id={getTestId('birth-date')}
          />
          {getError('birthDate') && (
            <Typography sx={{ color: '#df1b41', fontSize: '12px', mt: 0.5 }}>
              {getError('birthDate')}
            </Typography>
          )}
        </Box>
      </Box>

      {/* Relationship to Patient */}
      <Box sx={{ mb: 2 }}>
        <Typography component="label" sx={stripeLabelStyles}>
          Relationship to Patient
        </Typography>
        <select
          value={addressDetails.relationshipToPatient}
          onChange={(e) => onAddressDetailsChange('relationshipToPatient', e.target.value)}
          onBlur={() => handleBlur('relationshipToPatient')}
          style={getSelectStyle('relationshipToPatient')}
          test-id={getTestId('relationship-to-patient')}
          disabled={isPatientPayor}
        >
          <option value="">Select Relationship to Patient</option>
          {isPatientPayor ? (
            <option value="Self">Self</option>
          ) : (
            RELATIONSHIP_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))
          )}
        </select>
        {getError('relationshipToPatient') && (
          <Typography sx={{ color: '#df1b41', fontSize: '12px', mt: 0.5 }}>
            {getError('relationshipToPatient')}
          </Typography>
        )}
      </Box>

      {/* Street Address */}
      <Box sx={{ mb: 2 }}>
        <Typography component="label" sx={stripeLabelStyles}>
          Street Address
        </Typography>
        <input
          type="text"
          value={addressDetails.streetAddress1}
          onChange={(e) => onAddressDetailsChange('streetAddress1', e.target.value)}
          onBlur={() => handleBlur('streetAddress1')}
          placeholder="Enter street address"
          style={getInputStyle('streetAddress1')}
          test-id={getTestId('street-address')}
        />
        {getError('streetAddress1') && (
          <Typography sx={{ color: '#df1b41', fontSize: '12px', mt: 0.5 }}>
            {getError('streetAddress1')}
          </Typography>
        )}
      </Box>

      {/* City, State, Zip Row */}
      <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
        <Box sx={{ flex: 2 }}>
          <Typography component="label" sx={stripeLabelStyles}>
            City
          </Typography>
          <input
            type="text"
            value={addressDetails.city}
            onChange={(e) => onAddressDetailsChange('city', e.target.value)}
            onBlur={() => handleBlur('city')}
            placeholder="Enter city"
            style={getInputStyle('city')}
            test-id={getTestId('city')}
          />
          {getError('city') && (
            <Typography sx={{ color: '#df1b41', fontSize: '12px', mt: 0.5 }}>
              {getError('city')}
            </Typography>
          )}
        </Box>
        <Box sx={{ flex: 1 }}>
          <Typography component="label" sx={stripeLabelStyles}>
            State
          </Typography>
          <select
            value={addressDetails.state}
            onChange={(e) => onAddressDetailsChange('state', e.target.value)}
            onBlur={() => handleBlur('state')}
            style={getSelectStyle('state')}
            test-id={getTestId('state')}
          >
            <option value="">Select</option>
            {statesList.map((state) => (
              <option key={state.stateCode} value={state.stateCode}>
                {state.stateCode}
              </option>
            ))}
          </select>
          {getError('state') && (
            <Typography sx={{ color: '#df1b41', fontSize: '12px', mt: 0.5 }}>
              {getError('state')}
            </Typography>
          )}
        </Box>
        <Box sx={{ flex: 1 }}>
          <Typography component="label" sx={stripeLabelStyles}>
            Zip Code
          </Typography>
          <input
            type="text"
            value={addressDetails.zipCode}
            onChange={(e) => onAddressDetailsChange('zipCode', e.target.value)}
            onBlur={() => handleBlur('zipCode')}
            placeholder="Zip"
            style={getInputStyle('zipCode')}
            maxLength={5}
            test-id={getTestId('zip-code')}
          />
          {getError('zipCode') && (
            <Typography sx={{ color: '#df1b41', fontSize: '12px', mt: 0.5 }}>
              {getError('zipCode')}
            </Typography>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default GuarantorAddressFields;
